import { useEffect, useState } from "react";
import {
  FileText,
  Check,
  Download,
  Tag,
  Folder,
  Plus,
  X,
  ShieldCheck,
  Link2,
  FileDown,
  FileSpreadsheet,
  Clock,
  MessageSquare,
  Trash2,
  Pencil,
  Send,
  Copy,
  Info,
  Pen,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import SigningWorkflow from "@/components/SigningWorkflow";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Modal } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  EnvelopeStatusBadge,
  RecipientStatusBadge,
} from "@/components/ui/StatusBadge";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/useMobileUtils";
import {
  useAddTagToEnvelope,
  useRemoveTagFromEnvelope,
  useUpdateEnvelope,
} from "@/hooks/useQueries";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth.store";
import { formatDateShort, formatDateTime } from "@/utils/dateFormat";
import {
  envelopeApi,
  tagApi,
  folderApi,
  commentsApi,
} from "../../services/api";

export const EnvelopeDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const isMobile = useIsMobile();
  const [envelope, setEnvelope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState("");

  // Tags and folders state
  const [allTags, setAllTags] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [envelopeTags, setEnvelopeTags] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);

  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [notesLoading, setNotesLoading] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState("");

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);

  // Tag mutations
  const addTagMutation = useAddTagToEnvelope();
  const removeTagMutation = useRemoveTagFromEnvelope();

  // Envelope mutation (for folder changes)
  const updateEnvelopeMutation = useUpdateEnvelope();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [envelopeRes, tagsRes, foldersRes] = await Promise.all([
          envelopeApi.getById(id),
          tagApi.getAll(),
          folderApi.getAll(),
        ]);

        const envelopeData = envelopeRes.data.data;
        setEnvelope(envelopeData);
        setAllTags(tagsRes.data.data || []);
        setAllFolders(foldersRes.data.data || []);
        setSelectedFolder(envelopeData.folderId || null);

        // Fetch envelope tags
        try {
          const envelopeTagsRes = await tagApi.getEnvelopeTags(id);
          setEnvelopeTags(envelopeTagsRes.data.data || []);
        } catch {
          setEnvelopeTags([]);
        }

        // Fetch notes
        try {
          const notesRes = await envelopeApi.getNotes(id);
          setNotes(notesRes.data.data || []);
        } catch {
          setNotes([]);
        }

        // Fetch comments
        try {
          setCommentsLoading(true);
          const commentsRes = await commentsApi.list(id);
          setComments(commentsRes.data.data || commentsRes.data || []);
        } catch {
          setComments([]);
        } finally {
          setCommentsLoading(false);
        }
      } catch (error) {
        console.error("Failed to load envelope", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const handleAddTag = (tagId) => {
    const tag = allTags.find((t) => t.id === tagId);
    if (tag) {
      // Optimistically update UI
      setEnvelopeTags((prev) => [...prev, tag]);
    }

    addTagMutation.mutate(
      { envelopeId: id, tagId },
      {
        onError: () => {
          // Revert on error
          setEnvelopeTags((prev) => prev.filter((t) => t.id !== tagId));
        },
      },
    );
  };

  const handleRemoveTag = (tagId) => {
    const removedTag = envelopeTags.find((t) => t.id === tagId);

    // Optimistically update UI
    setEnvelopeTags((prev) => prev.filter((t) => t.id !== tagId));

    removeTagMutation.mutate(
      { envelopeId: id, tagId },
      {
        onError: () => {
          // Revert on error
          if (removedTag) {
            setEnvelopeTags((prev) => [...prev, removedTag]);
          }
        },
      },
    );
  };

  const handleFolderChange = (folderId) => {
    const previousFolder = selectedFolder;

    // Optimistically update UI
    setSelectedFolder(folderId || null);
    setEnvelope((prev) => ({ ...prev, folderId: folderId || null }));

    updateEnvelopeMutation.mutate(
      { id, data: { folderId: folderId || null } },
      {
        onError: () => {
          // Revert on error
          setSelectedFolder(previousFolder);
          setEnvelope((prev) => ({ ...prev, folderId: previousFolder }));
        },
      },
    );
  };

  // Note handlers
  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setNotesLoading(true);
    try {
      const response = await envelopeApi.addNote(id, newNote.trim());
      setNotes((prev) => [response.data.data, ...prev]);
      setNewNote("");
      toast.success(t("envelopeDetail.noteAdded", "Note added"));
    } catch (error) {
      console.error("Failed to add note", error);
      toast.error(t("envelopeDetail.noteAddFailed", "Failed to add note"));
    } finally {
      setNotesLoading(false);
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editNoteContent.trim()) return;

    setNotesLoading(true);
    try {
      const response = await envelopeApi.updateNote(
        id,
        noteId,
        editNoteContent.trim(),
      );
      setNotes((prev) =>
        prev.map((n) => (n.id === noteId ? response.data.data : n)),
      );
      setEditingNote(null);
      setEditNoteContent("");
      toast.success(t("envelopeDetail.noteUpdated", "Note updated"));
    } catch (error) {
      console.error("Failed to update note", error);
      toast.error(
        t("envelopeDetail.noteUpdateFailed", "Failed to update note"),
      );
    } finally {
      setNotesLoading(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    setNotesLoading(true);
    try {
      await envelopeApi.deleteNote(id, noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success(t("envelopeDetail.noteDeleted", "Note deleted"));
    } catch (error) {
      console.error("Failed to delete note", error);
      toast.error(
        t("envelopeDetail.noteDeleteFailed", "Failed to delete note"),
      );
    } finally {
      setNotesLoading(false);
    }
  };

  const startEditNote = (note) => {
    setEditingNote(note.id);
    setEditNoteContent(note.content);
  };

  const cancelEditNote = () => {
    setEditingNote(null);
    setEditNoteContent("");
  };

  const availableTags = allTags.filter(
    (tag) => !envelopeTags.some((et) => et.id === tag.id),
  );

  const handleResend = async (recipientId) => {
    setActionLoading(true);
    try {
      await envelopeApi.resendToRecipient(id, recipientId);
      const response = await envelopeApi.getById(id);
      setEnvelope(response.data.data);
    } catch (error) {
      console.error("Failed to resend", error);
    }
    setActionLoading(false);
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) return;

    setActionLoading(true);
    try {
      await envelopeApi.void(id, voidReason);
      const response = await envelopeApi.getById(id);
      setEnvelope(response.data.data);
      setShowVoidModal(false);
    } catch (error) {
      console.error("Failed to void envelope", error);
    }
    setActionLoading(false);
  };

  const handleDuplicate = async () => {
    setActionLoading(true);
    try {
      const response = await envelopeApi.duplicate(id);
      const newEnvelope = response.data.data;
      toast.success(t("envelopeDetail.duplicated", "Envelope duplicated"));
      navigate(`/envelopes/${newEnvelope.id}`);
    } catch (error) {
      console.error("Failed to duplicate envelope", error);
      toast.error(
        t("envelopeDetail.duplicateFailed", "Failed to duplicate envelope"),
      );
    }
    setActionLoading(false);
  };

  const handleDownload = async (documentId, type = "original") => {
    try {
      const response = await envelopeApi.downloadDocument(id, documentId, type);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `document.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download", error);
    }
  };

  // Export audit log as CSV
  const handleExportCSV = async () => {
    try {
      const response = await envelopeApi.exportAuditLog(id, "csv");
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit-log-${envelope.title || id}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(t("envelopeDetail.auditExported", "Audit log exported"));
    } catch (error) {
      console.error("Failed to export audit log", error);
      toast.error(
        t("envelopeDetail.auditExportFailed", "Failed to export audit log"),
      );
    }
  };

  // Export audit log as PDF
  const handleExportPDF = async () => {
    try {
      const response = await envelopeApi.exportAuditLog(id, "json");
      const { data } = response;

      // Generate PDF using browser print
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error(
          t("envelopeDetail.popupBlocked", "Please allow popups to export PDF"),
        );
        return;
      }

      const html = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Audit Log - ${envelope.title || "Envelope"}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { font-size: 24px; margin-bottom: 8px; }
            .subtitle { color: #666; margin-bottom: 24px; font-size: 14px; }
            .info { margin-bottom: 24px; padding: 16px; background: #f5f5f5; border-radius: 8px; }
            .info-row { display: flex; margin-bottom: 8px; }
            .info-label { font-weight: 600; width: 120px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { padding: 12px; text-align: left; border-bottom: 1px solid #e0e0e0; }
            th { background: #f5f5f5; font-weight: 600; }
            .event-time { font-size: 12px; color: #666; }
            .footer { margin-top: 32px; font-size: 12px; color: #666; text-align: center; border-top: 1px solid #e0e0e0; padding-top: 16px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <h1>Audit Log</h1>
          <p class="subtitle">${envelope.title || "Untitled Envelope"}</p>

          <div class="info">
            <div class="info-row"><span class="info-label">Envelope ID:</span> ${envelope.id}</div>
            <div class="info-row"><span class="info-label">Status:</span> ${envelope.status}</div>
            <div class="info-row"><span class="info-label">Created:</span> ${new Date(envelope.createdAt).toLocaleString()}</div>
            ${envelope.completedAt ? `<div class="info-row"><span class="info-label">Completed:</span> ${new Date(envelope.completedAt).toLocaleString()}</div>` : ""}
          </div>

          <table>
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Event</th>
                <th>Actor</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              ${data.events
                .map(
                  (e) => `
                <tr>
                  <td class="event-time">${new Date(e.timestamp).toLocaleString()}</td>
                  <td><strong>${e.event}</strong></td>
                  <td>${e.actor}</td>
                  <td>${e.details}${e.ip ? ` (IP: ${e.ip})` : ""}</td>
                </tr>
              `,
                )
                .join("")}
            </tbody>
          </table>

          <div class="footer">
            Generated on ${new Date().toLocaleString()} by eScribe
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(html);
      printWindow.document.close();

      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.print();
      };

      toast.success(t("envelopeDetail.auditExported", "Audit log exported"));
    } catch (error) {
      console.error("Failed to export audit log", error);
      toast.error(
        t("envelopeDetail.auditExportFailed", "Failed to export audit log"),
      );
    }
  };

  const renderRecipientStatusClass = (recipient) => {
    if (recipient?.status === "signed") {
      return "bg-success/10 text-success";
    }
    return recipient?.status === "declined"
      ? "bg-destructive/10 text-destructive"
      : "bg-muted text-muted-foreground";
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-48 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!envelope) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {t("envelopeDetail.notFound")}
        </h2>
        <Button onClick={() => navigate("/envelopes")}>
          {t("envelopeDetail.backToEnvelopes")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold font-heading text-foreground">
              {envelope.title}
            </h1>
            <EnvelopeStatusBadge status={envelope?.status} />
          </div>
          <p className="text-muted-foreground text-sm sm:text-base">
            {t("envelopeDetail.created")}{" "}
            {formatDateShort(envelope.createdAt, user)} ·
            {t("envelopeDetail.updated")}{" "}
            {formatDateShort(envelope.updatedAt, user)}
            {envelope.expiresAt && (
              <>
                {" "}
                ·{" "}
                <span
                  className={
                    new Date(envelope.expiresAt) < new Date()
                      ? "text-destructive"
                      : "text-amber-600"
                  }
                >
                  <Clock className="inline-block w-3 h-3 mr-1" />
                  {new Date(envelope.expiresAt) < new Date()
                    ? t("envelopeDetail.expired", "Expired")
                    : t("envelopeDetail.expiresOn", "Expires {{date}}", {
                        date: formatDateShort(envelope.expiresAt, user),
                      })}
                </span>
              </>
            )}
          </p>
        </div>

        {/* Desktop Actions */}
        <div className="hidden sm:flex gap-2">
          {["draft", "sent", "viewed", "partially_signed"].includes(
            envelope.status,
          ) && (
            <Button
              variant="outline"
              onClick={() => navigate(`/envelopes/${id}/signing-links`)}
            >
              <Link2 className="mr-2 h-4 w-4" />
              Signing Links
            </Button>
          )}
          {envelope.status === "draft" && (
            <Button onClick={() => navigate(`/envelopes/${id}/edit`)}>
              <Pen className="mr-2 h-4 w-4" />
              {t("envelopeDetail.editAndSend")}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={handleDuplicate}
            disabled={actionLoading}
          >
            <Copy className="h-4 w-4 mr-2" />
            {t("envelopeDetail.duplicate", "Duplicate")}
          </Button>
          {["sent", "viewed", "partially_signed"].includes(envelope.status) && (
            <Button variant="outline" onClick={() => setShowVoidModal(true)}>
              {t("envelopeDetail.void")}
            </Button>
          )}
        </div>
      </div>

      {/* Tags and Folder */}
      <Card className="p-5">
        <div className="flex flex-col sm:flex-row sm:items-start gap-6">
          {/* Folder Selection */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Folder className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                {t("folders.title")}
              </span>
            </div>
            <Select
              value={selectedFolder || "none"}
              onValueChange={(value) =>
                handleFolderChange(value === "none" ? null : value)
              }
            >
              <SelectTrigger className="w-full sm:w-50">
                <SelectValue placeholder={t("envelopeDetail.selectFolder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  {t("envelopeDetail.noFolder")}
                </SelectItem>
                {allFolders.map((folder) => (
                  <SelectItem key={folder.id} value={folder.id}>
                    <span className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: folder.color || "#6366f1" }}
                      />
                      {folder.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tags */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Tag className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">
                {t("tags.title")}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {envelopeTags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant="secondary"
                  className="flex items-center gap-1 pr-1"
                  style={{
                    backgroundColor: `${tag.color}20`,
                    color: tag.color,
                    borderColor: tag.color,
                  }}
                >
                  {tag.name}
                  <button
                    onClick={() => handleRemoveTag(tag.id)}
                    className="ml-1 hover:bg-black/10 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}

              {availableTags.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 gap-1">
                      <Plus className="h-3 w-3" />
                      {t("envelopeDetail.addTag")}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {availableTags.map((tag) => (
                      <DropdownMenuItem
                        key={tag.id}
                        onClick={() => handleAddTag(tag.id)}
                        className="flex items-center gap-2"
                      >
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tag.color || "#6366f1" }}
                        />
                        {tag.name}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {envelopeTags.length === 0 && availableTags.length === 0 && (
                <span className="text-sm text-muted-foreground">
                  {t("envelopeDetail.noTagsAvailable")}
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Progress */}
      {["sent", "viewed", "partially_signed"].includes(envelope.status) && (
        <Card className="p-5">
          <h3 className="font-medium text-foreground mb-4">
            {t("envelopeDetail.signingProgress")}
          </h3>
          <div className="flex items-center space-x-4">
            {envelope.recipients
              ?.filter((r) => r.role === "signer")
              .map((recipient, index) => (
                <div key={recipient.id} className="flex items-center">
                  {index > 0 && <div className="w-8 h-0.5 bg-border mx-2" />}
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center",
                      renderRecipientStatusClass(recipient),
                    )}
                  >
                    {recipient.status === "signed" ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </Card>
      )}

      {/* Documents */}
      <Card>
        <CardHeader className="py-4 px-5 border-b border-border">
          <h3 className="font-medium text-foreground">
            {t("envelopeDetail.documents")}
          </h3>
        </CardHeader>
        <div className="divide-y divide-border">
          {envelope.documents?.map((doc) => (
            <div key={doc.id} className="p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-destructive/10 rounded flex items-center justify-center">
                  <FileText className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <p className="font-medium text-foreground">{doc.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {t("envelopeDetail.pages", { count: doc.pageCount })}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownload(doc.id, "original")}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t("common.download")}
                  </Button>
                  {envelope.status !== "completed" && (
                    <span
                      className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 bg-muted rounded-full"
                      title={t(
                        "envelopeDetail.watermarkApplied",
                        "Watermark applied to draft documents",
                      )}
                    >
                      <Info className="w-3 h-3 text-muted-foreground" />
                    </span>
                  )}
                </div>
                {envelope.status === "completed" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(doc.id, "signed")}
                  >
                    {t("envelopeDetail.downloadSigned")}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Signing Workflow */}
      {envelope.recipients?.length > 0 && (
        <Card>
          <CardHeader className="py-4 px-5 border-b border-border">
            <h3 className="font-medium text-foreground">
              {t("envelopeDetail.signingWorkflow", "Signing Workflow")}
            </h3>
          </CardHeader>
          <CardContent className="p-5">
            <SigningWorkflow envelope={envelope} user={user} />
          </CardContent>
        </Card>
      )}

      {/* Recipients */}
      <Card>
        <CardHeader className="py-4 px-5 border-b border-border">
          <h3 className="font-medium text-foreground">
            {t("envelopeDetail.recipients")}
          </h3>
        </CardHeader>
        <div className="divide-y divide-border">
          {envelope.recipients?.map((recipient) => (
            <div
              key={recipient.id}
              className="p-4 flex items-center justify-between"
            >
              <div className="flex items-center space-x-3">
                <Avatar>
                  <AvatarFallback className="bg-primary/10 text-primary">
                    {recipient.name?.charAt(0)?.toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-foreground">
                      {recipient.name}
                    </p>
                    {recipient.consentGivenAt && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <ShieldCheck className="w-4 h-4 text-success" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs text-primary-foreground">
                              {t("envelopeDetail.consentGiven")}
                            </p>
                            <p className="text-xs text-primary-foreground/70">
                              {formatDateTime(recipient.consentGivenAt, user)}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {recipient.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <RecipientStatusBadge status={recipient?.status} />
                {["sent", "delivered", "viewed"].includes(recipient.status) &&
                  ["sent", "viewed", "partially_signed"].includes(
                    envelope.status,
                  ) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResend(recipient.id)}
                      loading={actionLoading}
                    >
                      {t("envelopeDetail.resend")}
                    </Button>
                  )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Internal Notes */}
      <Card>
        <CardHeader className="py-4 px-5 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <h3 className="font-medium text-foreground">
                {t("envelopeDetail.notes.title", "Internal Notes")}
              </h3>
            </div>
            <span className="text-xs text-muted-foreground">
              {t(
                "envelopeDetail.notes.hint",
                "Only visible to you and your team",
              )}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          {/* Add new note */}
          <div className="flex gap-2 mb-4">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder={t(
                "envelopeDetail.notes.placeholder",
                "Add a note...",
              )}
              className="min-h-15 flex-1"
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  handleAddNote();
                }
              }}
            />
            <Button
              onClick={handleAddNote}
              disabled={!newNote.trim() || notesLoading}
              size="sm"
              className="self-end"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>

          {/* Notes list */}
          <div className="space-y-3">
            {notes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {t("envelopeDetail.notes.empty", "No notes yet")}
              </p>
            ) : (
              notes.map((note) => (
                <div
                  key={note.id}
                  className="p-3 bg-muted/50 rounded-lg border border-border"
                >
                  {editingNote === note.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editNoteContent}
                        onChange={(e) => setEditNoteContent(e.target.value)}
                        className="min-h-15"
                        autoFocus
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={cancelEditNote}
                        >
                          {t("common.cancel")}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleUpdateNote(note.id)}
                          disabled={!editNoteContent.trim() || notesLoading}
                        >
                          {t("common.save")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm whitespace-pre-wrap">
                        {note.content}
                      </p>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="font-medium">
                            {note.author?.name || note.author?.email}
                          </span>
                          <span>·</span>
                          <span>{formatDateTime(note.createdAt, user)}</span>
                          {note.updatedAt !== note.createdAt && (
                            <>
                              <span>·</span>
                              <span className="italic">
                                {t("common.edited", "edited")}
                              </span>
                            </>
                          )}
                        </div>
                        {note.userId === user?.id && (
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => startEditNote(note)}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteNote(note.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Comments from Signers */}
      {(comments.length > 0 || commentsLoading) && (
        <Card>
          <CardHeader className="py-4 px-5 border-b border-border">
            <h3 className="font-medium text-foreground flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              {t("envelopeDetail.comments", "Comments from Signers")}
              {comments.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {comments.length}
                </Badge>
              )}
            </h3>
          </CardHeader>
          <CardContent className="p-5">
            {commentsLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              <div className="space-y-3">
                {comments.map((comment) => (
                  <div key={comment.id} className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs">
                            {comment.authorName?.charAt(0)?.toUpperCase() ||
                              "?"}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-sm">
                          {comment.authorName}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(comment.createdAt, user)}
                      </span>
                    </div>
                    <p className="text-sm mt-2">{comment.content}</p>
                    {comment.pageNumber && (
                      <Badge variant="secondary" className="mt-2 text-xs">
                        {t("comments.page", "Page")} {comment.pageNumber}
                      </Badge>
                    )}
                    {comment.resolved && (
                      <Badge
                        variant="outline"
                        className="mt-2 ml-2 text-xs text-green-600"
                      >
                        {t("comments.resolved", "✓ Resolved")}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Activity Timeline */}
      <Card>
        <CardHeader className="py-4 px-5 border-b border-border flex flex-row items-center justify-between">
          <h3 className="font-medium text-foreground">
            {t("envelopeDetail.activity")}
          </h3>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                {t("envelopeDetail.exportAudit", "Export")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCSV}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                {t("envelopeDetail.exportCSV", "Export as CSV")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPDF}>
                <FileDown className="h-4 w-4 mr-2" />
                {t("envelopeDetail.exportPDF", "Export as PDF")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent className="p-5">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2" />
              <div>
                <p className="text-sm text-foreground">
                  {t("envelopeDetail.activityCreated")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(envelope.createdAt, user)}
                </p>
              </div>
            </div>
            {envelope.sentAt && (
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-success rounded-full mt-2" />
                <div>
                  <p className="text-sm text-foreground">
                    {t("envelopeDetail.activitySent")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(envelope.sentAt, user)}
                  </p>
                </div>
              </div>
            )}
            {/* Electronic Records Consent events */}
            {envelope.recipients
              ?.filter((r) => r.consentGivenAt)
              .map((recipient) => (
                <div
                  key={`consent-${recipient.id}`}
                  className="flex items-start space-x-3"
                >
                  <div className="w-2 h-2 bg-success rounded-full mt-2" />
                  <div>
                    <p className="text-sm text-foreground">
                      <ShieldCheck className="w-3.5 h-3.5 inline-block mr-1 text-success" />
                      {t("envelopeDetail.activityConsent", {
                        name: recipient.name,
                      }) ||
                        `${recipient.name} accepted Electronic Records Disclosure`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(recipient.consentGivenAt, user)}
                    </p>
                  </div>
                </div>
              ))}
            {/* Signed events */}
            {envelope.recipients
              ?.filter((r) => r.signedAt)
              .map((recipient) => (
                <div
                  key={`signed-${recipient.id}`}
                  className="flex items-start space-x-3"
                >
                  <div className="w-2 h-2 bg-success rounded-full mt-2" />
                  <div>
                    <p className="text-sm text-foreground">
                      {t("envelopeDetail.activitySigned", {
                        name: recipient.name,
                      }) || `${recipient.name} signed`}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(recipient.signedAt, user)}
                    </p>
                  </div>
                </div>
              ))}
            {envelope.completedAt && (
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-success rounded-full mt-2" />
                <div>
                  <p className="text-sm text-foreground">
                    {t("envelopeDetail.activityCompleted")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDateTime(envelope.completedAt, user)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Void Modal */}
      <Modal
        isOpen={showVoidModal}
        onClose={() => setShowVoidModal(false)}
        title={t("envelopeDetail.voidModal.title")}
      >
        <p className="text-muted-foreground mb-4">
          {t("envelopeDetail.voidModal.message")}
        </p>
        <Textarea
          value={voidReason}
          onChange={(e) => setVoidReason(e.target.value)}
          placeholder={t("envelopeDetail.voidModal.placeholder")}
          rows={3}
          className="mb-4"
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => setShowVoidModal(false)}
            size={isMobile ? "mobile" : "default"}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={handleVoid}
            loading={actionLoading}
            disabled={!voidReason.trim()}
            size={isMobile ? "mobile" : "default"}
          >
            {t("envelopeDetail.voidModal.confirm")}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default EnvelopeDetail;
